import React, { useState, useEffect, useRef } from "react";
import { CALENDLY_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";

const CalendlyTabs = () => {
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [inputUrl, setInputUrl] = useState("");
  const [calendlyId, setCalendlyId] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasFetchedUrl, setHasFetchedUrl] = useState(false);

  const calendlyRef = useRef(null);
  const { getValidToken } = useAuth();

  const loadCalendlyScript = () => {
    const existingScript = document.getElementById("calendly-script");
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.id = "calendly-script";
      script.async = true;
      document.body.appendChild(script);
    }
  };

  const loadCalendlyWidget = () => {
    if (calendlyRef.current && calendlyUrl) {
      calendlyRef.current.innerHTML = "";
      const iframe = document.createElement("iframe");
      iframe.src = calendlyUrl;
      iframe.width = "100%";
      iframe.height = "700px";
      iframe.frameBorder = "0";
      calendlyRef.current.appendChild(iframe);
    }
  };

  useEffect(() => {
    loadCalendlyScript();
    if (calendlyUrl) {
      loadCalendlyWidget();
    }
  }, [calendlyUrl]);

  useEffect(() => {
    const handleCalendlyMessage = (event) => {
      if (event.data?.event === "calendly.event_scheduled") {
        setConfirmed(true);
      }
    };
    window.addEventListener("message", handleCalendlyMessage);
    return () => window.removeEventListener("message", handleCalendlyMessage);
  }, []);

  useEffect(() => {
    const fetchCalendlyUrl = async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(CALENDLY_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-ID": localStorage.getItem("tenant"),
          },
        });

        const data = await res.json();
        if (data?.calendly_link) {
          setCalendlyUrl(data.calendly_link);
          setCalendlyId(data.id); // needed for PUT
        }
      } catch (error) {
        console.error("No existing Calendly link");
      } finally {
        setLoading(false);
        setHasFetchedUrl(true);
      }
    };

    fetchCalendlyUrl();
  }, []);

  const handleConnect = async () => {
    if (!inputUrl.startsWith("https://calendly.com/")) {
      alert("Please enter a valid Calendly URL.");
      return;
    }

    setCalendlyUrl(inputUrl);

    const method = calendlyId ? "PUT" : "POST";
    const url = calendlyId
      ? `${CALENDLY_ENDPOINT}${calendlyId}/`
      : CALENDLY_ENDPOINT;

    try {
      const token = await getValidToken();
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": localStorage.getItem("tenant"),
        },
        body: JSON.stringify({
          name: "Current User",
          calendly_link: inputUrl,
        }),
      });

      const data = await res.json();
      if (data?.id) setCalendlyId(data.id);
      alert("Calendly link saved successfully!");
    } catch (error) {
      console.error("Failed to save Calendly URL", error);
      alert("Failed to save Calendly URL.");
    }
  };

  if (loading)
    return <div style={{ padding: "2rem" }}>Loading Calendly...</div>;

  return (
    <div style={{ padding: "2rem", position: "relative" }}>
      <h2>📅 Book a Meeting</h2>

      {!calendlyUrl && hasFetchedUrl ? (
        <>
          <p>Enter your Calendly link below</p>
          <input
            type="url"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="https://calendly.com/your-username"
            style={{ padding: "0.5rem", width: "100%", marginBottom: "1rem" }}
          />

          <button
            onClick={handleConnect}
            style={{
              padding: "0.6rem 1.2rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            🔗 Connect to Calendly
          </button>
        </>
      ) : (
        <>
          <div style={{ margin: "1rem 0" }}>
            <strong>Your Calendly link :</strong>
            <br />
            <a href={calendlyUrl} target="_blank" rel="noreferrer">
              {calendlyUrl}
            </a>
          </div>

          {/* Single floating reload button */}
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <button
              onClick={() => {
                setConfirmed(false);
                loadCalendlyWidget();
              }}
              style={{
                padding: "0.6rem 1.2rem",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              🔄 Load your calender
            </button>
          </div>

          <div ref={calendlyRef}></div>
        </>
      )}
    </div>
  );
};

export default CalendlyTabs;
